import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { PENDING_BUSINESS_ID, PusherChannel, PusherEvent } from '@mondaygpt/constants'
import { useAnalytics } from 'hooks'
import { selectBusinessId } from 'modules/authentication'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { usePusher } from 'modules/core/contexts/pusher.context'
import React, { useEffect, useRef } from 'react'
import { CREDITS_LOW_THRESHOLD } from '../constants'
import {
  CreditBalance,
  selectBusinessEntitlement,
  selectTotalCredits,
  updateCreditBalance,
} from '../slices/credit-balance.slice'

export const CreditPusherHandler: React.FC = () => {
  const dispatch = useAppDispatch()
  const { track } = useAnalytics()
  const businessId = useAppSelector(selectBusinessId)
  const entitlement = useAppSelector(selectBusinessEntitlement)
  const currentTotal = useAppSelector(selectTotalCredits)
  const { subscribeToChannel, unsubscribeFromChannel, isConnected } = usePusher()
  const prevTotalRef = useRef(currentTotal)
  const creditsLowFiredRef = useRef(false)

  useEffect(() => {
    prevTotalRef.current = currentTotal
  }, [currentTotal])

  useEffect(() => {
    if (!businessId || businessId === PENDING_BUSINESS_ID || !isConnected) return

    const channelName = `${PusherChannel.Private}-${PusherChannel.Business}-${businessId}`
    const channel = subscribeToChannel(channelName)
    if (!channel) return

    const handler = (data: { balance: CreditBalance }) => {
      const newTotal = data.balance.totalCredits
      const wasAbove = prevTotalRef.current > CREDITS_LOW_THRESHOLD
      const isNowBelow = newTotal <= CREDITS_LOW_THRESHOLD && newTotal > 0

      // Low-balance analytics only (no UI nudge — the push surfaces are
      // reserved for the fully out-of-credits state per product decision).
      if (wasAbove && isNowBelow && !creditsLowFiredRef.current) {
        creditsLowFiredRef.current = true
        track(AnalyticsEvents.Credits.CREDITS_LOW, {
          credits_remaining: newTotal,
          tier: entitlement?.tier || 'Free',
        })
      }

      if (newTotal > CREDITS_LOW_THRESHOLD) {
        creditsLowFiredRef.current = false
      }

      dispatch(updateCreditBalance(data.balance))
    }

    channel.bind(PusherEvent.CreditBalanceUpdated, handler)

    return () => {
      channel.unbind(PusherEvent.CreditBalanceUpdated, handler)
      unsubscribeFromChannel(channelName)
    }
  }, [
    businessId,
    isConnected,
    subscribeToChannel,
    unsubscribeFromChannel,
    dispatch,
    track,
    entitlement,
  ])

  return null
}
